import { useState, useCallback, useEffect, useRef } from "react";
import api from "../services/api";
import { getCurrentPositionSafe } from "../utils/geolocation";
import { generateIdempotencyKey } from "../utils/idempotencyKey";
import { compressImage } from "../utils/compressImage";

/**
 * useCheckin
 *
 * Encapsula o estado e a lógica de check-in de treino:
 * - flags de modal (showCheckinModal, showCardioModal)
 * - estado de loading/erro (checkingIn, checkinError)
 * - computed: selectedWorkoutAssignment, selectedCheckinTarget
 * - funções: openCheckin, getWorkoutCheckinTarget, confirmCheckin
 *
 * Nota: openWorkoutCta e startSessionForAssignment cruzam as fronteiras de
 * useWorkoutSession e useConfirmDialog — ficam em Dashboard como coordenadores.
 *
 * @param {object}   opts
 * @param {object}   opts.user                       - usuário logado { id }
 * @param {function} opts.setToast                   - callback({ type, message })
 * @param {Array}    opts.exercises                  - lista de exercícios
 * @param {Array}    opts.assignedWorkouts            - planos atribuídos ao aluno
 * @param {string}   opts.selectedWorkoutAssignmentId
 * @param {function} opts.setSelectedWorkoutAssignmentId
 * @param {object}   opts.selectedChallenge          - desafio selecionado para check-in
 * @param {function} opts.setSelectedChallenge
 * @param {function} opts.setSelectedExercise
 * @param {object}   opts.form                       - estado do formulário de check-in
 * @param {function} opts.setForm
 * @param {object}   opts.workoutSession             - sessão ativa de treino
 * @param {function} opts.allExercisesCompleted      - (assignment) => boolean
 * @param {number}   opts.elapsedSeconds             - segundos decorridos na sessão
 * @param {function} opts.setTotalPoints
 * @param {function} opts.clearWorkoutSession
 * @param {function} opts.setWorkoutSummary
 * @param {function} opts.loadData                   - recarrega dados do dashboard
 * @param {function} opts.getErrorMessage            - (err) => string
 * @param {function} opts.openConfirm                 - openConfirm() do useConfirmDialog
 *                                                       compartilhado do Dashboard (usado pro
 *                                                       aviso de check-in com horário
 *                                                       fisicamente implausível — ver
 *                                                       _check_checkin_overlap_or_raise em
 *                                                       app/services/user_service.py)
 */
export function useCheckin({
  user,
  setToast,
  exercises,
  assignedWorkouts,
  selectedWorkoutAssignmentId,
  setSelectedWorkoutAssignmentId,
  selectedChallenge,
  setSelectedChallenge,
  setSelectedExercise,
  form,
  setForm,
  workoutSession,
  allExercisesCompleted,
  elapsedSeconds,
  setTotalPoints,
  clearWorkoutSession,
  setWorkoutSummary,
  loadData,
  getErrorMessage,
  openConfirm,
}) {
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [showCardioModal, setShowCardioModal]   = useState(false);
  const [checkingIn, setCheckingIn]             = useState(false);
  const [checkinError, setCheckinError]         = useState("");

  // R221: dispara a captura de geolocalizacao assim que o modal de check-in
  // ABRE, nao so quando o usuario confirma. O CardioCheckinModal.jsx ja fazia
  // isso (useEffect no mount) e nunca teve o problema abaixo; aqui a chamada
  // vivia dentro de confirmCheckin(), so no clique de "Finalizar" — ou seja,
  // sem nenhum tempo de "aquecimento" antes do timeout de 10s de
  // getCurrentPositionSafe(). Caso real (aluno 51, 28/08): check-in
  // PRESCRITO ficou sem localizacao, e o check-in de CARDIO AVULSO 31s
  // depois (mesmo dispositivo, mesma permissao ja concedida) pegou a
  // localizacao normalmente — o padrao classico de um primeiro fix de GPS
  // frio estourando os 10s de timeout, seguido de um segundo pedido que se
  // beneficia do fix ja "quente"/em cache do sistema. Nao e falta de
  // permissao. Pedindo a localizacao no OPEN do modal (em paralelo com o
  // aluno preenchendo duracao/selecionando desafio) da ao fix de GPS o mesmo
  // tempo extra que o fluxo de cardio ja tinha de graca. Ver
  // docs/07-issues.md R221.
  const locationPromiseRef = useRef(null);

  useEffect(() => {
    if (showCheckinModal) {
      locationPromiseRef.current = getCurrentPositionSafe();
    }
  }, [showCheckinModal]);

  // Chave de idempotência: uma por abertura do modal, reaproveitada em todo
  // retry dessa mesma tentativa (inclusive o "Tentar novamente" do
  // checkinError abaixo e a reconfirmação após o aviso de sobreposição) —
  // evita duplicar o check-in quando o POST anterior já tinha sido
  // processado no servidor mas a resposta não chegou de volta (queda de
  // rede). Ver docs/07-issues.md e generateIdempotencyKey.
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    idempotencyKeyRef.current = showCheckinModal ? generateIdempotencyKey() : null;
  }, [showCheckinModal]);

  // O rótulo textual da localização (nome da academia/rua) NÃO vem pronto
  // no primeiro loadData() após o check-in: é resolvido em BackgroundTask
  // no servidor (reverse geocoding via Nominatim + Google Places/Overpass,
  // até ~20s no pior caso quando os serviços externos estão lentos) pra não
  // segurar a resposta do POST /checkin — ver resolve_checkin_location_label_bg
  // em app/services/user_service.py. O lat/lon já vai no loadData() logo
  // abaixo, então o pino de localização aparece na hora, só o texto (antes
  // "Ver localização" genérico) que ficava esperando o aluno dar um refresh
  // manual na página pra aparecer. Reforça mais algumas rodadas de loadData()
  // nos segundos seguintes só quando essa checkin teve localização capturada,
  // pra pegar o rótulo assim que o backend terminar de resolvê-lo.
  const labelRefreshTimersRef = useRef([]);

  useEffect(() => {
    return () => {
      labelRefreshTimersRef.current.forEach(clearTimeout);
      labelRefreshTimersRef.current = [];
    };
  }, []);

  // ── Computed ────────────────────────────────────────────────────────────────
  const selectedWorkoutAssignment =
    assignedWorkouts.find(
      (a) => String(a.id) === String(selectedWorkoutAssignmentId)
    ) || null;

  const selectedCheckinTarget = selectedWorkoutAssignment;

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Retorna o exercício-alvo e a contagem de itens para o check-in de um plano.
   * Usa o primeiro item com exercise_id (ordem asc por order_index).
   */
  const getWorkoutCheckinTarget = useCallback(
    (assignment) => {
      const items = [...(assignment?.items || [])].sort(
        (a, b) => Number(a.order_index || 0) - Number(b.order_index || 0)
      );
      const firstItem = items.find((item) => item.exercise_id);
      if (!firstItem) return null;
      const exercise = exercises.find((item) => item.id === firstItem.exercise_id) || null;
      if (!exercise) return null;
      return { exercise, itemsCount: items.length };
    },
    [exercises]
  );

  /**
   * Abre o modal de check-in para um desafio específico (clique no card do desafio).
   */
  const openCheckin = (c) => {
    const challengeId = c.challenge_id ?? c.id;
    if (!challengeId) {
      setToast({ type: "error", message: "Desafio inválido para check-in" });
      return;
    }
    setSelectedExercise(null);
    setSelectedWorkoutAssignmentId(
      assignedWorkouts[0]?.id ? String(assignedWorkouts[0].id) : ""
    );
    setCheckinError("");
    setForm((prev) => ({ ...prev, duration: "", weight: "", photo: null }));
    setSelectedChallenge({ ...c, challenge_id: challengeId });
    setShowCheckinModal(true);
  };

  /**
   * Submete o check-in de treino.
   * Calcula duração pelo timer quando todos os exercícios foram concluídos.
   * Finaliza a sessão ativa no backend e constrói o resumo pós-treino.
   */
  const confirmCheckin = async () => {
    if (checkingIn) return;

    setCheckinError("");

    if (!selectedWorkoutAssignment) {
      setToast({ type: "info", message: "Selecione um treino para registrar" });
      return;
    }

    const workoutTarget = getWorkoutCheckinTarget(selectedWorkoutAssignment);
    if (!workoutTarget) {
      setToast({
        type: "error",
        message: "Esse treino não possui exercícios suficientes para registro",
      });
      return;
    }

    const exerciseTarget = workoutTarget.exercise;

    // Usa o timer quando todos os exercícios estão concluídos
    let durationToUse = form.duration;
    if (workoutSession && allExercisesCompleted(selectedWorkoutAssignment)) {
      const timerMin = Math.round(elapsedSeconds / 60);
      durationToUse = String(timerMin || form.duration || "");
    }

    if (!durationToUse || Number(durationToUse) <= 0) {
      setToast({ type: "info", message: "Informe a duração do treino em minutos" });
      return;
    }

    // Envia o check-in. Se o backend detectar que a duração informada não
    // cabe no intervalo real desde o check-in anterior do aluno (409
    // checkin_overlap — ver _check_checkin_overlap_or_raise em
    // app/services/user_service.py), abre um diálogo de confirmação em vez
    // de bloquear; se o aluno confirmar mesmo assim, reenvia com
    // confirm_overlap=true (o backend loga essa confirmação pra permitir
    // identificar recorrência depois).
    const doSubmit = async (confirmOverlap) => {
      try {
        setCheckingIn(true);

        // Reaproveita o pedido disparado na abertura do modal (ver comentario
        // no useEffect acima) — so cai pra um pedido novo se por algum motivo
        // o efeito nao rodou (ex.: chamada programatica que pula o setState).
        // É uma Promise já cacheada em ref, então reusar em uma segunda
        // chamada (confirm_overlap=true) não dispara um novo pedido de GPS.
        const location = await (locationPromiseRef.current ?? getCurrentPositionSafe());

        // Foto reduzida no aparelho antes do upload (R271); se não der, vai
        // a original e o backend reduz, como antes.
        const photo = form.photo ? await compressImage(form.photo) : null;

        const formData = new FormData();
        formData.append("duration", durationToUse);
        if (photo) formData.append("photo", photo);
        if (confirmOverlap) formData.append("confirm_overlap", "true");
        formData.append(
          "idempotency_key",
          idempotencyKeyRef.current || (idempotencyKeyRef.current = generateIdempotencyKey())
        );
        if (selectedWorkoutAssignmentId)
          formData.append("user_workout_plan_id", selectedWorkoutAssignmentId);
        if (workoutSession?.id)
          formData.append("workout_session_id", workoutSession.id);
        if (location) {
          formData.append("checkin_lat", location.lat);
          formData.append("checkin_lon", location.lon);
          if (location.accuracy != null) {
            formData.append("checkin_accuracy", location.accuracy);
          }
        }

        const url = selectedChallenge
          ? `/users/${user.id}/challenges/${selectedChallenge.challenge_id}/checkin/${exerciseTarget.id}`
          : `/users/${user.id}/checkin/${exerciseTarget.id}`;

        const res = await api.post(url, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          // Seguro repetir em 503: o idempotency_key faz o backend descartar
          // a duplicata (ver services/retry503.js).
          retryOn503: true,
        });

        // Finaliza a sessão ativa no backend (await — garante que finished_at seja gravado)
        // A resposta traz os recordes pessoais batidos no treino, usados no
        // resumo logo abaixo (ver app/services/personal_records.py).
        let finishRes = null;
        if (workoutSession?.id) {
          try {
            // Se tem tempo manual (exercícios não finalizados), usa /finish-now para zerar séries pendentes
            const hasManualTime = !allExercisesCompleted(selectedWorkoutAssignment) && form.duration && Number(form.duration) > 0;
            if (hasManualTime) {
              finishRes = await api.post(`/workout-sessions/${workoutSession.id}/finish-now`);
            } else {
              finishRes = await api.patch(`/workout-sessions/${workoutSession.id}/finish`, {
                total_duration_minutes: Number(durationToUse),
              });
            }
          } catch (err) {
            // Não bloqueia o fluxo de checkin, mas loga para diagnóstico
            console.error("Erro ao finalizar sessão no servidor:", err);
          }
        }

        setTotalPoints(res.data.total_points);

        // Toasts suprimidos: toda informação de pontos e conclusão já aparece
        // no modal de resumo (WorkoutSummaryModal) que abre logo a seguir.

        // Monta resumo pós-treino antes de limpar a sessão
        if (workoutSession) {
          const durationSecs = Math.round(
            (Date.now() - (workoutSession.startedAt || Date.now())) / 1000
          );
          const setsLog    = workoutSession.setsLog || {};
          const completions = workoutSession.exerciseCompletions || {};
          const completedIds = Object.keys(completions);
          let totalSets = 0;
          let totalVolume = 0;
          const exerciseRows = [];

          (selectedWorkoutAssignment?.items || []).forEach((item) => {
            const sid = String(item.id);
            const sets = (setsLog[sid] || []).filter((s) => s.done);
            // inclui exercícios com pelo menos 1 série feita OU marcados como concluídos
            if (!completions[sid] && sets.length === 0) return;
            totalSets += sets.length;
            let vol = 0;
            sets.forEach((s) => {
              if (s.weight) {
                const repsNum = parseFloat(
                  String(s.reps || "0").match(/[\d.]+/)?.[0] || "0"
                );
                if (repsNum > 0) vol += repsNum * s.weight;
              }
            });
            totalVolume += vol;
            exerciseRows.push({
              exerciseItemId: item.exercise_item_id ?? item.exercise_item?.id ?? null,
              name: item.exercise_item?.name || item.name || "Exercício",
              sets: sets.length,
              volume: Math.round(vol),
            });
          });

          setWorkoutSummary({
            // Nome do treino e data: identificam o treino no print que os
            // alunos postam (WorkoutSummaryModal).
            workoutName:
              selectedWorkoutAssignment?.custom_name ||
              selectedWorkoutAssignment?.workout_plan?.title ||
              null,
            finishedAt: new Date().toISOString(),
            durationSecs,
            totalExercises: completedIds.length,
            totalSets,
            totalVolume: Math.round(totalVolume),
            exercises: exerciseRows,
            pointsAwarded: res.data.points_awarded,
            volumeBonus: res.data.volume_bonus,
            // Desafios vigentes que esse check-in NAO contou (categoria, duracao
            // minima ou geofence) — ver _checkin_exclusion_reasons em
            // user_service.py. So informativo, nunca bloqueia o check-in.
            challengeNotes: res.data.challenge_notes,
            personalRecords: finishRes?.data?.personal_records || [],
            // Volume da última vez que fez este mesmo treino (null na 1ª vez)
            previousVolume: finishRes?.data?.previous_volume ?? null,
            // Dias treinados na semana (faixa S T Q Q S S D do resumo)
            week: finishRes?.data?.week ?? null,
          });
        }

        clearWorkoutSession();
        setShowCheckinModal(false);
        setSelectedExercise(null);
        setSelectedWorkoutAssignmentId("");
        setForm((prev) => ({ ...prev, duration: "", weight: "", photo: null }));
        loadData();

        // Ver comentário no useEffect de labelRefreshTimersRef acima: sem
        // location capturada não há rótulo a esperar, e o job em background
        // nem é disparado pelo backend nesse caso (checkin_lat/lon nulos).
        if (location) {
          labelRefreshTimersRef.current.forEach(clearTimeout);
          // Janela alargada: o comentário de resolve_checkin_location_label_bg
          // (app/services/user_service.py) fala em "até ~12s só o Overpass,
          // mais Nominatim e Google antes" — ou seja, a cadeia inteira pode
          // passar de 20s no pior caso (relato real: 11/09, ficou em
          // "Ver localização" até o aluno sair e voltar do PWA). 20s era perto
          // demais do limite; agora cobre até ~45s, folga de mais de 2x sobre
          // o pior caso documentado.
          labelRefreshTimersRef.current = [4000, 8000, 14000, 22000, 32000, 45000].map((delay) =>
            setTimeout(() => { loadData(); }, delay)
          );
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const isOverlap =
          err?.response?.status === 409 &&
          detail && typeof detail === "object" && detail.code === "checkin_overlap";

        if (isOverlap && !confirmOverlap) {
          // Primeira tentativa detectada como fisicamente implausível: pede
          // confirmação em vez de bloquear (ver _check_checkin_overlap_or_raise).
          openConfirm?.({
            title: "Confirmar horário do check-in",
            message: detail.message,
            confirmLabel: "Confirmar mesmo assim",
            cancelLabel: "Revisar horário",
            // "success" (não "primary") — é o único variant do ConfirmDialog
            // com a cor de marca (--blue/--accent, #1d9e75) consistente nos
            // dois temas; "primary" tem um azul hardcoded no tema claro que
            // não bate com o branding (ver ConfirmDialog.module.css).
            confirmVariant: "success",
            onConfirm: () => doSubmit(true),
          });
          return;
        }

        const message = getErrorMessage(err);
        setCheckinError(message);
        setToast({ type: "error", message });
      } finally {
        setCheckingIn(false);
      }
    };

    await doSubmit(false);
  };

  return {
    // estado
    showCheckinModal, setShowCheckinModal,
    showCardioModal, setShowCardioModal,
    checkingIn,
    checkinError, setCheckinError,
    // computed
    selectedWorkoutAssignment,
    selectedCheckinTarget,
    // ações
    openCheckin,
    getWorkoutCheckinTarget,
    confirmCheckin,
  };
}
